(async () => {
  const baseComponent = await import(window.theme.modules.baseComponent);

  // ========================================================================
  // CART POOL STATE MANAGEMENT
  // ========================================================================

  // Load initial pool state from Liquid-rendered JSON
  function loadCartPoolState() {
    const el = document.getElementById('cart-pool-state');
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent);

      // Recalculate pool totals from itemWeights
      // (Liquid's pre-sum may be wrong due to duplicate key handling)
      if (data.pools && data.itemWeights) {
        for (const key of Object.keys(data.pools)) {
          data.pools[key].totalUsed = 0;
        }
        for (const [variantId, itemData] of Object.entries(data.itemWeights)) {
          const key = itemData.productId + '_' + itemData.poolType;
          if (data.pools[key]) {
            data.pools[key].totalUsed += itemData.totalWeight;
          }
        }
      }

      return data;
    } catch (e) {
      console.error('Failed to parse cart pool state:', e);
      return null;
    }
  }

  // Recalculate pool totals from current cart-item elements
  function recalculatePoolTotals() {
    const state = window.cartPoolState;
    if (!state?.pools) return;

    // Reset all pool totals to 0
    for (const key of Object.keys(state.pools)) {
      state.pools[key].totalUsed = 0;
    }

    // Sum weights from all cart-items currently in DOM
    document.querySelectorAll('cart-item').forEach(item => {
      const productId = item.getAttribute('cart-item-product-id');
      const poolType = item.getAttribute('data-pool-type');
      const variantWeight = parseFloat(item.getAttribute('data-variant-weight')) || 0;
      const qtyInput = item.querySelector('[data-input]');
      const quantity = qtyInput ? parseInt(qtyInput.value) || 0 : 0;

      if (!productId || !poolType) return;

      const key = productId + '_' + poolType;
      if (state.pools[key]) {
        state.pools[key].totalUsed += variantWeight * quantity;
      } else {
        // New product/pool combination not in initial state
        // Get pool limit from the item's data attributes
        const poolLimit = poolType === 'smalls'
          ? parseFloat(item.getAttribute('data-pool-smalls')) || 0
          : poolType === 'tops'
          ? parseFloat(item.getAttribute('data-pool-tops')) || 0
          : poolType === 'trim'
          ? parseFloat(item.getAttribute('data-pool-trim')) || 0
          : parseFloat(item.getAttribute('data-pool-kief')) || 0;
        const isM2O = item.getAttribute('data-is-m2o') === 'true';

        state.pools[key] = {
          productId: productId,
          poolType: poolType,
          totalUsed: variantWeight * quantity,
          poolLimit: poolLimit,
          isM2O: isM2O
        };
      }
    });
  }

  // Update all cart-item '+' buttons based on current pool state
  function updateAllPoolButtons() {
    document.querySelectorAll('cart-item').forEach(item => {
      if (item._updatePlusButtonState) {
        item._updatePlusButtonState();
      }
    });
  }

  // Initialize global pool state
  window.cartPoolState = loadCartPoolState();

  customElements.define('main-cart', class extends baseComponent.default {
      elements = {
        items: 'cart-items',
        subtotal: 'subtotal-price',
        form: '#cart-form',
        checkoutButton: '[name="checkout"]'
      };

      render() {
        this.listenTo('cartChange', (e, data) => {
          const source = data.html.querySelector('main-cart');

          source.hasAttribute('is-empty')
            ? this.setAttribute('is-empty', '')
            : this.removeAttribute('is-empty');

          this.$subtotal.replaceChildren(...data.html.querySelector(this.elements.subtotal).childNodes);

          // Clear checkout error on successful cart update
          this._clearCheckoutError();
        });

        // Listen for validation errors from cart items
        this.listenTo('cartValidationError', (e, data) => {
          this._showCheckoutError(data.message);
        });

        // Intercept checkout form submission
        if (this.$form && this.$checkoutButton) {
          this.$form.addEventListener('submit', (e) => {
            if (e.submitter && e.submitter.name === 'checkout') {
              e.preventDefault();
              this._handleCheckout();
            }
          });
        }
      }

      _handleCheckout() {
        // Check for existing visible errors - block checkout and shake them
        const visibleErrors = this.querySelectorAll('[data-checkout-error], [data-cart-item-error]');
        let hasVisibleError = false;
        visibleErrors.forEach(el => {
          if (el.style.display !== 'none' && el.style.display !== '') {
            hasVisibleError = true;
          }
        });

        if (hasVisibleError) {
          this._shakeErrors();
          return; // Block checkout until errors are resolved
        }

        this.$checkoutButton.setAttribute('loading', '');

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sections: this.sectionId })
        })
        .then(res => {
          if (!res.ok) return res.json().then(err => Promise.reject(err));
          this.$checkoutButton.removeAttribute('loading');
          window.location.href = '/checkout';
        })
        .catch(error => {
          console.log('Checkout validation error:', error);
          this.$checkoutButton.removeAttribute('loading');
          const message = error.description || error.message || error.error ||
            (Array.isArray(error.errors) ? error.errors[0] : error.errors) || 'Unable to checkout.';
          if (message.toLowerCase().includes('insufficient') ||
              message.toLowerCase().includes('inventory')) {
            this._showCheckoutError(message);
            this._shakeErrors();
          } else {
            window.location.href = '/checkout';
          }
        });
      }

      _showCheckoutError(message) {
        let errorEl = this.querySelector('[data-checkout-error]');
        if (!errorEl) {
          errorEl = document.createElement('div');
          errorEl.setAttribute('data-checkout-error', '');
          errorEl.className = '#cart-validation-error';
          errorEl.setAttribute('role', 'alert');
          const buttonsEl = this.querySelector('[data-checkout-buttons]') ||
                            this.querySelector('.\\#cart-checkout-buttons');
          if (buttonsEl) buttonsEl.before(errorEl);
        }
        errorEl.innerHTML = `
          <svg aria-hidden="true" class="#icon" viewBox="0 0 13 13">
            <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"/>
            <path d="M5.87 3.52h1.26l-.18 4.54H6.05l-.18-4.54zm.63 5.36c-.41 0-.74.32-.74.72 0 .4.33.72.74.72.41 0 .74-.32.74-.72 0-.4-.33-.72-.74-.72z" fill="white"/>
          </svg>
          <span>${this._escapeHtml(message)}</span>
        `;
        errorEl.style.display = 'flex';
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      _clearAllErrors() {
        this.querySelectorAll('[data-checkout-error], [data-cart-item-error]').forEach(el => {
          el.style.display = 'none';
        });
      }

      _clearCheckoutError() {
        const errorEl = this.querySelector('[data-checkout-error]');
        if (errorEl) {
          errorEl.style.display = 'none';
          errorEl.innerHTML = '';
        }
      }

      _shakeErrors() {
        const errors = this.querySelectorAll('[data-checkout-error], [data-cart-item-error]');
        errors.forEach(el => {
          if (el.style.display !== 'none' && el.style.display !== '') {
            // Remove class first to restart animation
            el.classList.remove('shake');
            // Force reflow to restart animation
            void el.offsetWidth;
            el.classList.add('shake');
            // Remove class after animation completes
            setTimeout(() => el.classList.remove('shake'), 500);
          }
        });
      }

      _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      update(callback) {
        fetch(`${this.getAttribute('cart-url')}?section_id=${this.sectionId}`).then((response) => {
          return response.text();
        })
        .then((text) => {
          const parsedHtml = new DOMParser().parseFromString(text, 'text/html');
          const source = parsedHtml.querySelector('main-cart');

          source.hasAttribute('is-empty')
            ? this.setAttribute('is-empty', '')
            : this.removeAttribute('is-empty');

          this.$items.replaceChildren(...source.querySelector(this.elements.items).childNodes);
          this.$subtotal.replaceChildren(...source.querySelector(this.elements.subtotal).childNodes);
          callback();
        })
        .catch((e) => { console.error(e)});
      }
  });

  customElements.define('s-subtotal-price', class extends baseComponent.default {
    render() {
      this.listenTo('cartChange', (e, data) => {
        const source = data.html.querySelector('subtotal-price');
        this.replaceChildren(...source.childNodes);
      })
    }
  });

  customElements.define('cart-item', class extends baseComponent.default {
    elements = {
      plusButton: '[data-inc]',
      minusButton: '[data-dec]',
      removeButton: '[data-remove]',
      qtyInput: '[data-input]',
      linePrice: '[data-total]'
    }

    render() {
      this.sectionId = this.getAttribute('section-id');
      this.lineIndex = this.getAttribute('line-item-index');
      this.variantId = this.getAttribute('cart-item-variant-id');
      this.qtyValue = +this.$qtyInput.value;
      this.variantQtyLimit = +this.$qtyInput.max;
      this._handleInteractions();
      this._updatePlusButtonState();
    };

    _handleInteractions() {
      this.$plusButton.addEventListener('click', () => this._validateInput(1));
      this.$minusButton.addEventListener('click', () => this._validateInput(-1));
      this.$qtyInput.addEventListener('change', () => this._validateInput());
      this.$removeButton.addEventListener('click', this._removeItem.bind(this));
    };

    _validateInput(input) {
      const value = input ? this.qtyValue + input : +this.$qtyInput.value;
      if (value < 1 || (this.variantQtyLimit && value > this.variantQtyLimit)) {
        this.$qtyInput.value = this.qtyValue;
        return
      }
      this.qtyValue = value;
      this._updateUI();
      this._updateCartRequest(value);
    };

    _removeItem(e) {
      e.preventDefault();
      this._updateCartRequest(0);
    };

    _updateUI() {
      this._updateInputValue();
      this._updateButtons();
    };

    _updateInputValue() {
      this.$qtyInput.value = this.qtyValue;
    };

    _updateButtons() {
      this.$plusButton.disabled = this.qtyValue === this.variantQtyLimit;
      this.$minusButton.disabled = this.qtyValue === 1;
    };

    _updateCartRequest (qty) {
      this.setAttribute('loading', '');
      this._clearItemError();
      this.previousQty = this.qtyValue;
      const body = JSON.stringify({
        id: this.variantId,
        quantity: qty,
        sections: this.sectionId,
        sections_url: this.getAttribute('cart-url')
      });
      const fetchConfig = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': `application/json` }
      };
      fetch(`${routes.cart_change_url}`, {...fetchConfig, ...{ body }})
        .then((response) => {
          if (!response.ok) {
            return response.json().then(err => Promise.reject(err));
          }
          return response.json();
        })
        .then((state) => {
          const html = state.sections[this.sectionId];
          const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
          this._acceptChanges(parsedHtml, qty);
          this.removeAttribute('loading');
          // Recalculate pool totals and update ALL cart-item buttons
          recalculatePoolTotals();
          updateAllPoolButtons();
        })
        .catch((error) => {
          console.log('Cart item validation error:', error);
          this.removeAttribute('loading');
          this.qtyValue = this.previousQty;
          this._updateInputValue();
          this._updateButtons();
          const message = error.description || error.message || error.error ||
            (Array.isArray(error.errors) ? error.errors[0] : error.errors) ||
            'Unable to update quantity.';
          this._showItemError(message);
          this.forwardEvent('cartValidationError', { message, variantId: this.variantId });
        });
    };

    _showItemError(message) {
      let errorEl = this.querySelector('[data-cart-item-error]');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.setAttribute('data-cart-item-error', '');
        errorEl.className = '#cart-item-error';
        errorEl.setAttribute('role', 'alert');
        // Insert INSIDE qty wrapper, after controls (so it appears below)
        const qtyEl = this.querySelector('[data-item-qty]') ||
                      this.querySelector('.\\#cart-item-qty');
        if (qtyEl) qtyEl.appendChild(errorEl);
      }
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    };

    _clearItemError() {
      const errorEl = this.querySelector('[data-cart-item-error]');
      if (errorEl) errorEl.style.display = 'none';
    };

    _updatePlusButtonState() {
      const poolType = this.getAttribute('data-pool-type');
      const variantWeight = parseFloat(this.getAttribute('data-variant-weight')) || 0;
      const productId = this.getAttribute('cart-item-product-id');
      const isM2O = this.getAttribute('data-is-m2o') === 'true';

      if (!poolType || poolType === '') return; // Not a pooled variant
      if (poolType === 'tops' && isM2O) return; // M2O tops never pool-limited

      // Get shared pool state
      const state = window.cartPoolState;
      const key = productId + '_' + poolType;
      const poolInfo = state?.pools?.[key];

      if (!poolInfo) {
        // Fallback to individual item check if no shared state
        const poolLimit = poolType === 'smalls'
          ? parseFloat(this.getAttribute('data-pool-smalls')) || 0
          : poolType === 'tops'
          ? parseFloat(this.getAttribute('data-pool-tops')) || 0
          : poolType === 'trim'
          ? parseFloat(this.getAttribute('data-pool-trim')) || 0
          : parseFloat(this.getAttribute('data-pool-kief')) || 0;
        const currentWeight = this.qtyValue * variantWeight;
        const nextWeight = (this.qtyValue + 1) * variantWeight;
        const shouldDisable = nextWeight > poolLimit;

        if (shouldDisable) {
          this.$plusButton.setAttribute('data-pool-disabled', 'true');
        } else {
          this.$plusButton.removeAttribute('data-pool-disabled');
        }
        return;
      }

      // Check if adding ONE MORE of this variant would exceed pool
      // totalUsed already includes this item's current quantity
      const remaining = poolInfo.poolLimit - poolInfo.totalUsed;
      const shouldDisable = variantWeight > remaining;

      if (shouldDisable) {
        this.$plusButton.setAttribute('data-pool-disabled', 'true');
      } else {
        this.$plusButton.removeAttribute('data-pool-disabled');
      }
    };

    _acceptChanges(html, qty) {
      this.forwardEvent('cartChange', {
        html: html,
        remove: qty === 0,
        variantId: this.variantId
      });
      if (qty === 0) {
        this.remove();
        return;
      }
      this._updateLinePrice(html);
    };

    _updateLinePrice(html) {
      const source = html.querySelector(`[data-total="${this.variantId}"]`);
      this.$linePrice.replaceChildren(...source.childNodes);
    };
  });

  // Initialize pool button states after all cart-items have rendered
  if (window.cartPoolState) {
    // Small delay to ensure cart-items have rendered
    setTimeout(() => {
      recalculatePoolTotals();
      updateAllPoolButtons();
    }, 100);
  }

})();
